import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { CompanyContext } from '@models/webapi/user-profile';
import { FULL_ACCESS_DENIED_ROUTE, FULL_SELECT_COMPANY_ROUTE } from '@routes';
import { CompanyContextService, EytcAuthService } from '@services';
import { LoadingService } from '@services/loader/loading.service';
import { combineLatest, filter, finalize, map } from 'rxjs';

export const companyContextGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const destroyRef = inject(DestroyRef);

  const authService = inject(EytcAuthService);
  const companyContextService = inject(CompanyContextService);
  const loadingService = inject(LoadingService);

  loadingService.show();

  return combineLatest([
    toObservable(authService.userProfile),
    toObservable(companyContextService.currentCompany),
  ]).pipe(
    filter(([userProfile]) => userProfile !== null),
    map(([userProfile, companyContext]) => {
      const companies = userProfile?.companies.map(c => c.toCompanyContext()) ?? [];
      let result = false;

      if (companies.length === 0) {
        console.warn('No companies found for user profile, redirecting to access denied');
        redirectToAccessDenied(router);
        return false;
      }

      if (companies.length === 1) {
        result = handleUserWithSignleCompany(companies[0], companyContext, router, state, companyContextService);
      } else {
        result = handleUserWithMultipleCompanies(companies, companyContext, router, state);
      }

      return result;
    }),
    finalize(() => {
      loadingService.hide();
    }),
    takeUntilDestroyed(destroyRef)
  );
};

function handleUserWithSignleCompany(
  company: CompanyContext,
  companyContext: CompanyContext | null,
  router: Router,
  state: RouterStateSnapshot,
  companyContextService: CompanyContextService
) {
  if (!companyContext) {
    companyContextService.currentCompany.set(company);
    return true;
  }

  if (companyContext!.id !== company.id) {
    redirectToSelectCompanyScreen(router, state);
    return false;
  }

  return true;
}

function handleUserWithMultipleCompanies(
  companies: CompanyContext[],
  companyContext: CompanyContext | null,
  router: Router,
  state: RouterStateSnapshot
) {
  if (!companyContext) {
    redirectToSelectCompanyScreen(router, state);
    return false;
  }

  const userProfileCompany = companies.find(company => company.id === companyContext?.id);

  if (!userProfileCompany) {
    redirectToSelectCompanyScreen(router, state);
    return false;
  }

  return true;
}

function redirectToSelectCompanyScreen(router: Router, state: RouterStateSnapshot) {
  void router.navigate([FULL_SELECT_COMPANY_ROUTE], {
    queryParams: { returnUrl: state.url },
  });
}

function redirectToAccessDenied(router: Router) {
  void router.navigate([FULL_ACCESS_DENIED_ROUTE], {
    state: {
      checkUser: false,
      sourceUrl: window.location.href,
    },
  });
}
